import { DataSnapshot, ModelOffering, InferenceProfile } from '@bedrock-profiles/shared';
import { profileToModelOffering } from '../utils/modelOfferings';
import generatedOpenAIOfferings from './openAIModelOfferings.generated.json';

export const DEFAULT_DEV_OPENAI_MOCK_LIMIT = 400;

const STATIC_ADDITIONAL_OFFERINGS: ModelOffering[] = [];

const GENERATED_OPENAI_MOCK_OFFERINGS = generatedOpenAIOfferings as ModelOffering[];
const SORTED_GENERATED_OPENAI_MOCK_OFFERINGS = [...GENERATED_OPENAI_MOCK_OFFERINGS].sort((a, b) =>
  a.offeringId.localeCompare(b.offeringId)
);

function getMockOfferings(maxGeneratedOfferings?: number): ModelOffering[] {
  const cappedGenerated =
    maxGeneratedOfferings && maxGeneratedOfferings > 0
      ? SORTED_GENERATED_OPENAI_MOCK_OFFERINGS.slice(0, maxGeneratedOfferings)
      : SORTED_GENERATED_OPENAI_MOCK_OFFERINGS;

  return [...cappedGenerated, ...STATIC_ADDITIONAL_OFFERINGS];
}

function offeringToProfile(offering: ModelOffering): InferenceProfile {
  return {
    profileId: offering.offeringId,
    profileName: offering.displayName,
    cloudProvider: offering.deployment.cloudProvider,
    profileArn: offering.offeringId,
    provider: offering.model.provider,
    modelName: offering.pricingModelName || offering.model.modelId,
    description: offering.description,
    sourceRegion: offering.deployment.sourceRegion,
    destinationRegions: offering.deployment.destinationRegions,
    status: offering.status,
    createdAt: offering.createdAt,
    lastUpdated: offering.lastUpdated,
    regionalGroup: offering.deployment.regionalGroup as InferenceProfile['regionalGroup'],
    providerSpecific: offering.providerSpecific,
  };
}

export function injectOpenAIMockData(
  snapshot: DataSnapshot,
  options?: { maxGeneratedOfferings?: number }
): DataSnapshot {
  const openAIMockOfferings = getMockOfferings(options?.maxGeneratedOfferings);
  const existingOfferings =
    snapshot.modelOfferings && snapshot.modelOfferings.length > 0
      ? snapshot.modelOfferings
      : snapshot.profiles.map(profileToModelOffering);

  const offeringMap = new Map<string, ModelOffering>();
  existingOfferings.forEach((offering) => offeringMap.set(offering.offeringId, offering));
  openAIMockOfferings.forEach((offering) => offeringMap.set(offering.offeringId, offering));

  const mergedOfferings = Array.from(offeringMap.values());

  const profileMap = new Map<string, InferenceProfile>();
  snapshot.profiles.forEach((profile) => profileMap.set(profile.profileId, profile));
  openAIMockOfferings.map(offeringToProfile).forEach((profile) => profileMap.set(profile.profileId, profile));

  const mergedProfiles = Array.from(profileMap.values());

  const referencedRegions = new Set<string>();
  mergedOfferings.forEach((offering) => {
    if (offering.deployment.sourceRegion) {
      referencedRegions.add(offering.deployment.sourceRegion);
    }
    (offering.deployment.destinationRegions || []).forEach((region) => referencedRegions.add(region));
  });

  const providerCounts = mergedOfferings.reduce<Record<string, number>>((counts, offering) => {
    counts[offering.model.provider] = (counts[offering.model.provider] || 0) + 1;
    return counts;
  }, {});

  const cloudProviderCounts = mergedOfferings.reduce<Record<string, number>>((counts, offering) => {
    counts[offering.deployment.cloudProvider] = (counts[offering.deployment.cloudProvider] || 0) + 1;
    return counts;
  }, {});

  return {
    ...snapshot,
    modelOfferings: mergedOfferings,
    profiles: mergedProfiles,
    metadata: {
      ...snapshot.metadata,
      totalProfiles: mergedOfferings.length,
      totalRegions: Math.max(snapshot.metadata.totalRegions, referencedRegions.size),
      providerCounts,
      modelPublisherCounts: providerCounts,
      cloudProviderCounts,
      inferenceProviderCounts: cloudProviderCounts,
    },
  };
}
